import { decayedLevel } from "@bindery/shared/contracts/decay";
import {
  getWordReadingFluencyLevel,
  WordReadingFluencyEnum,
} from "@bindery/shared/contracts/fluency";
import {
  CORPUS_VOCAB_SIZE,
  MAX_EXCLUDE_TEXT_IDS,
  MAX_EXTRA_WORDS,
  MAX_WORD_LENGTH,
} from "@bindery/shared/corpus/selection";
import * as z from "zod";
import type { ReadingRecommendationsRequest } from "@/contracts/reading-recommendations";
import type { BookmarkValue, FluencyRow } from "@/storage/db";

export const MAX_KNOWN_WORDS = CORPUS_VOCAB_SIZE + MAX_EXTRA_WORDS;
export const MAX_DEVELOPING_WORDS = CORPUS_VOCAB_SIZE;
export const MAX_PREDICTED_WORDS = CORPUS_VOCAB_SIZE;

export type FluencySnapshot = Pick<
  FluencyRow,
  "word" | "history" | "serverHistory" | "lastUpdated"
>;

export type CompletionSnapshot = Pick<
  BookmarkValue,
  "readingId" | "completed" | "lastupdate"
>;

export interface RecommendationWordLists {
  knownWords: string[];
  developingWords: string[];
  predictedWords: string[];
}

type WordList = keyof RecommendationWordLists;

const LIST_CAPS: Record<WordList, number> = {
  knownWords: MAX_KNOWN_WORDS,
  developingWords: MAX_DEVELOPING_WORDS,
  predictedWords: MAX_PREDICTED_WORDS,
};

function listFor(level: WordReadingFluencyEnum): WordList | null {
  switch (level) {
    case WordReadingFluencyEnum.Strong:
    case WordReadingFluencyEnum.Learned:
    case WordReadingFluencyEnum.Initial:
      return "knownWords";
    case WordReadingFluencyEnum.Developing:
      return "developingWords";
    case WordReadingFluencyEnum.Predicted:
      return "predictedWords";
    case WordReadingFluencyEnum.Unknown:
      return null;
  }
}

function sameHistory(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((response, i) => response === b[i]);
}

// A local append leaves lastUpdated at the server's last stamp (0 for a word
// the server has never seen), so a word read on this device since then would
// decay as if untouched: a word tapped offline would age from Developing to
// Initial and be sent as known.
function touchedAt(row: FluencySnapshot, now: number): number {
  return sameHistory(row.history, row.serverHistory) ? row.lastUpdated : now;
}

const isSendable = (word: string): boolean =>
  word.length > 0 && word.length <= MAX_WORD_LENGTH;

/**
 * Each word in the list its level after decay puts it in, Unknown words in
 * none. Over a list's cap the most recently touched words are kept, ties by
 * word, so a large profile trims to the words the reader is using now.
 */
export function recommendationWordLists(
  rows: readonly FluencySnapshot[],
  now: number,
): RecommendationWordLists {
  const buckets: Record<WordList, { word: string; touchedAt: number }[]> = {
    knownWords: [],
    developingWords: [],
    predictedWords: [],
  };
  for (const row of rows) {
    if (!isSendable(row.word)) continue;
    const updatedAt = touchedAt(row, now);
    const level = decayedLevel({
      level: getWordReadingFluencyLevel(row.history),
      updatedAt,
      now,
    });
    const list = listFor(level);
    if (list !== null)
      buckets[list].push({ word: row.word, touchedAt: updatedAt });
  }
  const trimmed = (list: WordList): string[] =>
    [...buckets[list]]
      .sort(
        (a, b) =>
          b.touchedAt - a.touchedAt ||
          (a.word < b.word ? -1 : a.word > b.word ? 1 : 0),
      )
      .slice(0, LIST_CAPS[list])
      .map((entry) => entry.word);
  return {
    knownWords: trimmed("knownWords"),
    developingWords: trimmed("developingWords"),
    predictedWords: trimmed("predictedWords"),
  };
}

const textIdSchema = z.uuid();

/**
 * The reader's completed readings, most recently finished first and capped
 * at what the route accepts. Readings outside the corpus (the bundled
 * library's ids are not all UUIDs) could never be recommended, and one would
 * fail the whole request, so they are left out.
 */
export function excludedTextIds(
  bookmarks: readonly CompletionSnapshot[],
): string[] {
  const ids = bookmarks
    .filter(
      (bookmark) =>
        bookmark.completed === true &&
        textIdSchema.safeParse(bookmark.readingId).success,
    )
    .sort((a, b) => b.lastupdate - a.lastupdate)
    .map((bookmark) => bookmark.readingId);
  return [...new Set(ids)].slice(0, MAX_EXCLUDE_TEXT_IDS);
}

export function recommendationsRequest(
  fluency: readonly FluencySnapshot[],
  bookmarks: readonly CompletionSnapshot[],
  now: number,
): ReadingRecommendationsRequest {
  return {
    ...recommendationWordLists(fluency, now),
    excludeTextIds: excludedTextIds(bookmarks),
  };
}
